import RightSidebarButton from "@/features/Navigation/components/RightSidebarButton";
import { useApiMutation } from "@/api/hooks/useApiMutation";
import { validateDraft as validateDraftMutation } from "@/entities/DataAssociation/DataAssociation.mutations";
import clsx from "clsx";
import { useDraft } from "../../hooks/useDraft";
import ValidationCheck from "./ValidationCheck";
import { useEffect, useState } from "react";
import {
    DraftValidation,
    ValidationFormula,
} from "@/entities/DataAssociation/Draft.model";
import { showCommitPanelAtom } from "../../Labeling.state";
import { useSetAtom } from "jotai";
import { Spinner } from "@/ui/atoms/Spinner";
import { commitDraft as commitDraftMutation } from "@/entities/DataAssociation/DataAssociation.mutations";
import { rightDrawerOpenAtom } from "@/features/Navigation/state";
import { StatementType } from "@/entities/DataAssociation/File.model";

export function DraftCommitPanel() {
    const setShowCommitPanel = useSetAtom(showCommitPanelAtom);
    const setRightDrawerOpen = useSetAtom(rightDrawerOpenAtom);
    const [validations, setValidations] = useState<DraftValidation>({
        completeness: {
            status: "loading",
            messages: [],
        },
        accountNaming: {
            status: "loading",
            messages: [],
        },
        formulas: {
            status: "loading",
            messages: [],
        },
    });
    const isValid = !Object.values(validations).some(
        (validation) =>
            validation.status === "fail" || validation.status === "loading",
    );
    const {
        mutate: validate,
        isError,
        isPending: isValidatePending,
    } = useApiMutation(validateDraftMutation, {
        mutationKey: ["validateDraft"],
        onSuccess: (response) => {
            setValidations(response);
        },
    });
    const { mutate: commit, isPending } = useApiMutation(commitDraftMutation, {
        mutationKey: ["commitDraft"],
        onSuccess: () => {
            setShowCommitPanel(false);
            setRightDrawerOpen(false);
        },
    });
    const { draftWithEdits, derivedStatementTypes } = useDraft();

    useEffect(() => {
        validate({ draft: draftWithEdits });
    }, []);

    const defaultStatus = isValidatePending
        ? "loading"
        : isError
          ? "neutral"
          : undefined;

    return (
        <div
            className={clsx(
                "px-6",
                "absolute inset-0 flex min-h-0 flex-col overflow-hidden",
            )}
        >
            <div className="sticky my-6 flex flex-col">
                <RightSidebarButton />
                <h2 className="pageheading">Labeling Checklist</h2>
                <p className="body-s subdued">
                    The following checks must pass before the file can be marked
                    as labeled.{" "}
                </p>

                <p
                    className={clsx(
                        "body-micro subdued mt-1 text-red-400",
                        !isError && "invisible",
                    )}
                >
                    Something went wrong while validating the draft.
                </p>
            </div>

            <ul
                className={clsx(
                    "pb-30",
                    "flex flex-col",
                    "custom-scrollbar hide-scrollbar gap-4 overflow-y-auto",
                )}
            >
                <li>
                    <ValidationCheck
                        title="Completeness"
                        description="All rows must have an account name and label."
                        status={
                            defaultStatus || validations.completeness.status
                        }
                    />
                </li>
                <li>
                    <ValidationCheck
                        title="Account Naming"
                        description="All account names must be unique."
                        status={
                            defaultStatus || validations.accountNaming.status
                        }
                    />
                </li>
                {derivedStatementTypes.includes(
                    StatementType.INCOME_STATEMENT,
                ) && (
                    <>
                        <li>
                            <ValidationCheck
                                title="Net Income"
                                description="The sum of all rows labeled as net income must equal the reported total."
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.INCOME_STATEMENT_BALANCE,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Revenue"
                                description="The sum of all rows labeled as revenue must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.REVENUE,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Cost of Goods Sold"
                                description="The sum of all rows labeled as COGS must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.COST_OF_GOODS_SOLD,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Total Operating Expenses"
                                description="The sum of all rows labeled as operating expenses must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.TOTAL_OP_EX,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="EBITDA"
                                description="Derived EBITDA must reconcile to the reported EBIDTA"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.EBITDA,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                    </>
                )}
                {derivedStatementTypes.includes(
                    StatementType.BALANCE_SHEET,
                ) && (
                    <>
                        <li>
                            <ValidationCheck
                                title="Balance Sheet Balance"
                                description="Assets, Liabilities, and Equity must balance."
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.EBITDA,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Current Assets"
                                description="The sum of all rows labeled as current assets must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.CURRENT_ASSETS,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Non-Current Assets"
                                description="The sum of all rows labeled as non-current assets must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.NONCURRENT_ASSETS,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Current Liabilities"
                                description="The sum of all rows labeled as current liabilities must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.CURRENT_LIABILITIES,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Non-Current Liabilities"
                                description="The sum of all rows labeled as non-current liabilities must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.NONCURRENT_LIABILITIES,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Total Assets"
                                description="The sum of all rows labeled as assets must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.TOTAL_ASSETS,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Total Liabilities"
                                description="The sum of all rows labeled as liabilities must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.TOTAL_LIABILITIES,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                        <li>
                            <ValidationCheck
                                title="Total Equity"
                                description="The sum of all rows labeled as equity must equal the reported total"
                                status={
                                    defaultStatus ||
                                    (validations.formulas.messages.find(
                                        (message) =>
                                            message.formula ===
                                            ValidationFormula.TOTAL_EQUITY,
                                    )
                                        ? "fail"
                                        : validations.formulas.status ===
                                            "loading"
                                          ? "loading"
                                          : "pass")
                                }
                            />
                        </li>
                    </>
                )}
            </ul>
            <div
                className={clsx(
                    "bg-default w-full p-6",
                    "absolute inset-x-0 bottom-0",
                )}
            >
                {!isError && (
                    <button
                        className="primary xl w-full"
                        disabled={!isValid || isPending}
                        onClick={() => commit({ draft: draftWithEdits })}
                    >
                        Commit draft {isPending && <Spinner />}
                    </button>
                )}
                {isError && (
                    <button
                        className="primary xl w-full"
                        onClick={() => validate({ draft: draftWithEdits })}
                        disabled={isValidatePending}
                    >
                        Retry validation {isValidatePending && <Spinner />}
                    </button>
                )}
            </div>
        </div>
    );
}
